import type {
  PaginatedResult,
  TrainerClassTransactionRow,
  TrainerConductOverview,
  TrainerSessionTarget,
  TrainerTrainingBookingStatus,
  TrainerTrainingTransactionRow,
} from '@/features/trainer-performance/types/trainer-performance'
import type {
  PaginatedResultDto,
  TrainerConductFeeTransactionEntity,
  TrainerConductOverviewEntity,
  TrainerPerformanceEntity,
} from './model'

function coerceString(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return ''
}

function coerceNumber(v: unknown): number {
  if (v == null || v === '') return 0
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isNaN(n) ? 0 : n
  }
  return 0
}

function coerceNumberOrNull(v: unknown): number | null {
  if (v == null || v === '') return null
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isNaN(n) ? null : n
  }
  return null
}

function mapBookingStatus(
  entity: TrainerConductFeeTransactionEntity
): TrainerTrainingBookingStatus {
  const s = `${entity.status} ${entity.statusLabel}`.toUpperCase()
  if (s.includes('ABSENT') || s.includes('NOT PARTICIP') || s.includes('NOT PARTICIPATED')) {
    return 'Not Participants'
  }
  if (s.includes('COMPLETE') || s.includes('ATTEND') || entity.status === 'COMPLETED') {
    return 'Completed'
  }
  return 'Not Participants'
}

export function mapTrainerPerformanceToSessionTarget(
  entity: TrainerPerformanceEntity
): TrainerSessionTarget {
  return {
    targetSessions: entity.targetSessions,
    currentSessions: entity.currentSessions ?? 0,
    startDate: entity.startDate,
    endDate: entity.endDate,
    updatedAt: entity.updatedAt,
  }
}

export function mapConductOverviewToUi(
  entity: TrainerConductOverviewEntity
): TrainerConductOverview {
  return {
    totalConductFees: entity.totalConductFeesThb,
    conductFeeTraining: entity.conductFeeFromTrainingThb,
    conductFeeClasses: entity.conductFeeFromClassesThb,
    bookedSessions: entity.bookedSessions,
    attendedParticipants: entity.attendedParticipants,
    noShowParticipants: entity.noShowParticipants,
    cancelledSessions: entity.cancelledSessions,
    totalClassesConducted: entity.totalClassesConducted,
    completedClasses: entity.completedClasses,
    cancelledClasses: entity.cancelledClasses,
  }
}

export function mapFeeTransactionToTrainingRow(
  entity: TrainerConductFeeTransactionEntity
): TrainerTrainingTransactionRow {
  return {
    id: String(entity.id),
    bookingDate: entity.bookingDate,
    memberName: coerceString(entity.memberName),
    packageName: coerceString(entity.packageName),
    totalSessionsHours: coerceNumber(entity.totalSessionsHours),
    amount: coerceNumber(entity.amountThb),
    perSession: coerceNumber(entity.perSessionThb),
    status: mapBookingStatus(entity),
    statusLabel: entity.statusLabel,
    commissionValue: coerceNumberOrNull(entity.commissionValueThb),
    receiptNo: coerceString(entity.receiptNo),
  }
}

export function mapFeeTransactionToClassRow(
  entity: TrainerConductFeeTransactionEntity
): TrainerClassTransactionRow {
  const commission = coerceNumberOrNull(entity.commissionValueThb)
  return {
    id: String(entity.id),
    classDate: entity.bookingDate,
    className: coerceString(entity.className),
    receiptNo: coerceString(entity.receiptNo),
    status: entity.status,
    statusLabel: entity.statusLabel,
    commissionValue: commission ?? 0,
  }
}

export function mapPaginatedFeeTransactions<T>(
  dto: PaginatedResultDto<TrainerConductFeeTransactionEntity>,
  mapRow: (e: TrainerConductFeeTransactionEntity) => T
): PaginatedResult<T> {
  return {
    data: dto.data.map(mapRow),
    total: dto.pagination.total,
  }
}
